/* TeddyGo landing — gallery, size/offer picker, tabs, sticky ATC, reveal */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal on scroll */
  const revealEls = document.querySelectorAll('.tg-reveal');
  if (revealEls.length && 'IntersectionObserver' in window && !reduce) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    revealEls.forEach((el) => io.observe(el));
    setTimeout(() => revealEls.forEach((el) => el.classList.add('is-visible')), 2500);
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  /* Gallery */
  class TgGallery extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.tg-gallery__slide')];
      this.thumbs = [...this.querySelectorAll('.tg-thumb')];
      this.index = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.querySelector('.tg-gallery__nav--prev')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('.tg-gallery__nav--next')?.addEventListener('click', () => this.go(this.index + 1));
      let x0 = null;
      const main = this.querySelector('.tg-gallery__main');
      main?.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      main?.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });
      this.go(0);
    }
    go(i) {
      const n = this.slides.length; if (!n) return;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => {
        const active = k === this.index;
        s.classList.toggle('is-active', active);
        const v = s.querySelector('video');
        if (v) { if (active) { v.play().catch(() => {}); } else { v.pause(); } }
      });
      this.thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === this.index);
        if (k === this.index) t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduce ? 'auto' : 'smooth' });
      });
    }
  }
  if (!customElements.get('tg-gallery')) customElements.define('tg-gallery', TgGallery);

  /* Buy box: size + offer -> variant id, quantity, prices */
  class TgBuyBox extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(this.querySelector('[data-tg-variants]')?.textContent || '[]'); } catch { this.variants = []; }
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.qtyInput = this.form?.querySelector('input[name="quantity"]');
      this.sizeInputs = [...this.querySelectorAll('input[name="tg-size"]')];
      this.offerInputs = [...this.querySelectorAll('input[name="tg-offer"]')];
      this.sizeInputs.forEach((i) => i.addEventListener('change', () => this.update()));
      this.offerInputs.forEach((i) => i.addEventListener('change', () => this.update()));
      // Deep link ?size=M
      const want = new URLSearchParams(location.search).get('size');
      if (want) { const el = this.sizeInputs.find((i) => i.value.toLowerCase() === want.toLowerCase()); if (el && !el.disabled) el.checked = true; }
      this.update();
    }
    get variant() {
      const size = this.sizeInputs.find((i) => i.checked)?.value;
      return this.variants.find((v) => v.option === size) || this.variants[0];
    }
    get qty() { return parseInt(this.offerInputs.find((i) => i.checked)?.value || '1', 10); }
    update() {
      const v = this.variant; if (!v) return;
      const qty = this.qty;
      if (this.idInput) this.idInput.value = v.id;
      if (this.qtyInput) this.qtyInput.value = qty;
      // prices
      const priceEl = this.querySelector('[data-tg-price]');
      const compareEl = this.querySelector('[data-tg-compare]');
      const saveEl = this.querySelector('[data-tg-save]');
      const pick = qty >= 2 ? v.bundle : v;
      if (priceEl) priceEl.textContent = pick.price;
      if (compareEl) { compareEl.textContent = pick.compare || ''; compareEl.hidden = !pick.compare; }
      if (saveEl) { saveEl.textContent = pick.save ? `Save ${pick.save}%` : ''; saveEl.hidden = !pick.save; }
      // offer rows
      this.offerInputs.forEach((i) => {
        const row = i.closest('.tg-offer');
        const q = parseInt(i.value, 10);
        const p = q >= 2 ? v.bundle : v;
        row.querySelector('[data-tg-offer-price]').textContent = p.price;
        const c = row.querySelector('[data-tg-offer-compare]');
        if (c) c.textContent = p.compare || '';
      });
      // hint
      const hint = this.querySelector('[data-tg-size-hint]');
      if (hint) hint.textContent = v.hint || '';
      // sticky bar sync
      document.dispatchEvent(new CustomEvent('tg:variant', { detail: { price: pick.price, compare: pick.compare, title: v.title, available: v.available } }));
      // history param
      try { const u = new URL(location.href); u.searchParams.set('size', v.option); history.replaceState(null, '', u); } catch {}
      // availability
      const btn = this.form?.querySelector('button[name="add"]');
      if (btn) { btn.disabled = !v.available; if (!v.available) btn.querySelector('.tg-atc__label') && (btn.querySelector('.tg-atc__label').textContent = 'Sold out'); }
    }
  }
  if (!customElements.get('tg-buy-box')) customElements.define('tg-buy-box', TgBuyBox);

  /* Tabs */
  document.querySelectorAll('[data-tg-tabs]').forEach((root) => {
    const tabs = [...root.querySelectorAll('.tg-tab')];
    const panels = [...root.querySelectorAll('.tg-tabpanel')];
    const imgs = [...(root.closest('.tg-sizeguide__layout') || root.parentElement).querySelectorAll('[data-tg-size-img]')];
    const select = (i) => {
      tabs.forEach((t, k) => t.setAttribute('aria-selected', String(k === i)));
      panels.forEach((p, k) => p.classList.toggle('is-active', k === i));
      imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
    };
    tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
    select(0);
  });

  /* Size-guide "choose this size" buttons -> select size in buy box + scroll */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tg-pick-size]');
    if (!btn) return;
    const size = btn.dataset.tgPickSize;
    const box = document.querySelector('tg-buy-box');
    const input = box?.querySelector(`input[name="tg-size"][value="${CSS.escape(size)}"]`);
    if (input && !input.disabled) { input.checked = true; input.dispatchEvent(new Event('change')); }
    if (box) { e.preventDefault(); box.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); }
  });

  /* Reels sound toggle */
  document.querySelectorAll('.tg-reel__sound').forEach((b) => {
    b.addEventListener('click', () => {
      const v = b.parentElement.querySelector('video'); if (!v) return;
      v.muted = !v.muted; b.setAttribute('aria-pressed', String(!v.muted));
      b.querySelector('[data-on]').hidden = v.muted; b.querySelector('[data-off]').hidden = !v.muted;
    });
  });

  /* Sticky ATC */
  const sticky = document.querySelector('.tg-sticky');
  const buyBox = document.querySelector('tg-buy-box');
  if (sticky && buyBox) {
    const io = new IntersectionObserver((entries) => {
      const visible = entries[0].isIntersecting;
      sticky.classList.toggle('is-visible', !visible && window.scrollY > 200);
    }, { threshold: 0 });
    io.observe(buyBox);
    sticky.querySelector('[data-tg-sticky-btn]')?.addEventListener('click', () => {
      const btn = buyBox.querySelector('button[name="add"]');
      if (btn && !btn.disabled) btn.click();
      else buyBox.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    });
    document.addEventListener('tg:variant', (e) => {
      const p = sticky.querySelector('[data-tg-sticky-price]'); const c = sticky.querySelector('[data-tg-sticky-compare]');
      if (p) p.textContent = e.detail.price; if (c) { c.textContent = e.detail.compare || ''; c.hidden = !e.detail.compare; }
    });
  }
})();

/* Confetti on add to cart */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const colors = ['#F6D76B', '#1F3BD1', '#FF6A4D', '#FFFFFF'];
  function burst(x, y) {
    const c = document.createElement('canvas'); c.className = 'tg-confetti';
    c.width = innerWidth; c.height = innerHeight; document.body.appendChild(c);
    const ctx = c.getContext('2d');
    const parts = Array.from({ length: 90 }, () => ({
      x, y, vx: (Math.random() - 0.5) * 16, vy: -Math.random() * 14 - 4, g: 0.45,
      s: 6 + Math.random() * 6, r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
      col: colors[Math.floor(Math.random() * colors.length)], life: 70 + Math.random() * 30,
    }));
    let t = 0;
    (function frame() {
      ctx.clearRect(0, 0, c.width, c.height); t++;
      let alive = false;
      for (const p of parts) {
        if (t > p.life) continue; alive = true;
        p.vy += p.g; p.x += p.vx; p.y += p.vy; p.r += p.vr;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.fillStyle = p.col;
        ctx.globalAlpha = Math.max(0, 1 - t / p.life); ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6); ctx.restore();
      }
      if (alive) requestAnimationFrame(frame); else c.remove();
    })();
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#tg-atc, [data-tg-sticky-btn]');
    if (!btn || btn.disabled) return;
    const r = btn.getBoundingClientRect();
    setTimeout(() => burst(r.left + r.width / 2, r.top + r.height / 2), 350);
  });
})();
